import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import {
  clientExerciseMatrixQueryOptions,
  clientTrainedExercisesQueryOptions,
} from '../api/clientDetailQueries';
import { toMatrixSource, toTrainedExercises } from '../api/clientDetailSource';
import { buildMatrix, type MatrixView } from '../model/formaMatrix';

export interface FormaMatrixState {
  readonly view: MatrixView;
  readonly pending: boolean;
  readonly failed: boolean;
  readonly retry: () => void;
}

/** „Wszystkie ćwiczenia", read only while that view is open. */
export function useFormaMatrix(clientId: number, open: boolean): FormaMatrixState {
  const { t, i18n } = useTranslation();
  const lang = useContentLanguage();
  const matrix = useQuery({ ...clientExerciseMatrixQueryOptions(clientId, lang), enabled: open });
  const trained = useQuery(clientTrainedExercisesQueryOptions(clientId, lang));
  const unread = new Set(
    toTrainedExercises(trained.data)
      .filter((exercise) => exercise.unread)
      .map((exercise) => exercise.exerciseId),
  );
  return {
    view: buildMatrix(toMatrixSource(matrix.data), unread, { t, locale: i18n.language }),
    pending: open && (matrix.isPending || (matrix.isError && matrix.isFetching)),
    failed: matrix.isError,
    retry: () => {
      void matrix.refetch();
    },
  };
}

/** Warm the matrix while the pointer rests on „Wszystkie", so the view opens on an answer. */
export function usePrefetchFormaMatrix(clientId: number): () => void {
  const queryClient = useQueryClient();
  const lang = useContentLanguage();
  return () => {
    void queryClient.prefetchQuery(clientExerciseMatrixQueryOptions(clientId, lang));
  };
}
